import { Injectable } from '@angular/core';
import { Observable, of, Subject } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { AuthService } from '../auth.service';
import { ApiService } from '../api.service';
import { environment } from '../../environments/environment';
import memoizeObservable from '../utils/memoize-observable';
import { TeamsResponse } from '../interfaces/user-profile.interface';
import { TransactionStatus, TransactionTypes} from '../models/transactions-enum';
import { Countries } from '../utils/countries-enum';
import { DataObject } from '../interfaces/data-objects';
import Utils from '../utils/util';

@Injectable({
  providedIn: 'root'
})
export class UserProfileService {
  countryLists: DataObject[] = [];
  landingPageCountry: string = '';
  copyTransactionSubject = new Subject<any>();
  responseCopyTransactionSubject = new Subject<any>();

  constructor(
    private api: ApiService,
    private auth: AuthService
  ) { }

  saveUserSelectedCountry(country: DataObject): Observable<any> {
    return this.api.put(`/api/activedirectory/getcountries/`, country)
    .pipe(
      map(response => {
        return response;
      })
    );
  }

  selectCountry(event: any, isReload: boolean, isDeepLinking: boolean = false) {
    if (event && (event.label || event.value)) {
      if (this.countryLists.length === 0) {
        this.countryLists = this.getUserCountryListLocalStorage();
      }
      let selectedCountry: DataObject = {};
      this.countryLists.forEach((x: DataObject) => {
        if ((x.label === event.label
          && x.value === event.value
          && x.lang === event.lang)
          || x.label === event.label) {
          selectedCountry = x;
        }
      });
      if (selectedCountry && selectedCountry.label) {
        if (selectedCountry.label === this.getUserCountryLabel()) {
          this.landingPageCountry = selectedCountry.label;
          return;
        } else {
          this.landingPageCountry = selectedCountry.label;
          const previousLocale = window.localStorage.getItem('user_locale') || 'en-US';

          this.setCountryLocalStorage(selectedCountry.value);
          this.setCountryLabelLocalStorage(selectedCountry.label);
          this.setLocale(selectedCountry.locale);
          const port = selectedCountry.port || '8081';
          window.localStorage.setItem('port', port);

          this.saveUserSelectedCountry({
            country: event.value,
            lang: selectedCountry.lang,
            label: selectedCountry.label
          }).subscribe(response => {
            if (response && isReload) {
              if (environment.datadog.env === 'local') {
                window.location.reload();
              } else if (isDeepLinking) {
                window.location.reload();
              } else {
                // const userLocale = window.localStorage.getItem('user_locale');
                let userLocale = window.localStorage.getItem('user_locale');
                if (userLocale === 'en') {
                  userLocale = 'en-US';
                }
                if ((userLocale && userLocale !== 'en-US') || previousLocale !== 'en-US') {
                  window.location.href = environment.finishlineurl + userLocale;
                } else {
                  window.location.reload();
                }
              }
            }
          });
        }
      }
    }
  }

  getCountryObjectByURL(userLocale: string, countryCode: string): any {
    if (this.countryLists.length === 0) {
      this.countryLists = this.getUserCountryListLocalStorage();
    }
    const country: DataObject = this.countryLists.filter((country: any) => country.value === countryCode && country.locale === userLocale)[0];
    if (country && Utils.isObject(country)) {
      return { ...country };
    }
    return { label: null, value: countryCode, locale: null, lang: null, country_not_found: true };
  }

  getUserCountryLabel() {
    return this.getCountryLabelLocalStorage();
  }

  getEmployeeId() {
    if (this.auth.getAuthToken()) {
      const submitTimestamp = Date.now();
      const date = new Date(submitTimestamp);
      const month = ('0' + (date.getMonth() + 1)).slice(-2);   // getMonth() is zero based
      const dayOfMonth = ('0' + date.getDate()).slice(-2);
      const localDate = date.getFullYear() + '-' + month + '-' + dayOfMonth
      const endpoint = `/api/activedirectory/getemplid/?localDate=${localDate}`
      return this.api.get(endpoint)
      .pipe(
        map(response => {
          if (response && response.result && response.result.employeeNumber) {
            const profileDetails = response.result;
            this.setCountryLocalStorage(profileDetails.country);
            this.setUserLocaleBaseOnCountry(profileDetails);
            this.setUserLanguageLocalStorage(profileDetails.current_language);
            this.setCountryLabelLocalStorage(profileDetails.country_label);
            this.setCountryListLocalStorage(profileDetails.country_list);
            this.setEmployeeNumberLocalStorage(profileDetails.employeeNumber);
            this.setCurrencySymbolLocalStorage(profileDetails.currency_symbol);
            this.setCurrencyLabelLocalStorage(profileDetails.currency_label);
            this.setCurrencyDecimalsLocalStorage(profileDetails.currency_decimals);
            this.setRoleLocalStorage(profileDetails.role);
            this.setFinanceUserFlagStorage(profileDetails.is_financeuser);
            this.setSupportUserFlagStorage(profileDetails.is_supportuser);
            this.setSuperUserFlagStorage(profileDetails.is_superuser);
            this.setBusinessUserFlagStorage(profileDetails.is_businessuser);
            this.setMasterUserFlagStorage(profileDetails.is_masteruser);
            this.setWD3blockUpdateStorage(profileDetails.block);
            this.setCEPIntegration(profileDetails.cep_country);
            this.setExecutiveApprover(profileDetails.is_executive_approver);
            this.setExecutiveApprover1(profileDetails.is_executive_approver1);
            this.setExecutiveApprover2(profileDetails.is_executive_approver2);
            this.setSurveyFlag(profileDetails.is_survey_due);
            this.setClientIQIntegration(profileDetails);
            this.setSalesForceAPI(profileDetails.is_salesforce_api);
            this.setBlockSubmit(profileDetails.block_submit);
            this.setCEPApiIntegration(profileDetails.cep_api_integrations);
            if (!profileDetails.my_finance) {
              this.setMyFinanceLocalStorage('recd');
            } else {
              this.setMyFinanceLocalStorage(profileDetails.my_finance);
            }
            this.setIsMultiLaEnabled(profileDetails.is_multi_la_enabled);
            this.setIsValuationEnabled(profileDetails.is_valuation_enabled);
            this.setIsDataCenterEnabled(profileDetails.is_data_center_enabled);
            this.setUserEmailLocalStorage(profileDetails.email);
            return response;
          }
        }
      ));
    }
    return of([]);
  }

  getBrokerDetails(employeeId: string) {
    return this.api.get(`/api/activedirectory/fetchbroker/${employeeId}/`)
    .pipe(
      map(response => {
        if (response) {
          return response.result;
        }
      })
    );
  }

  searchBroker(name: string) {
    return this.api.get(`/api/activedirectory/searchbroker/${name}`)
    .pipe(
      map(response => {
        if (response) {
          return response.result;
        }
      })
    );
  }

  getTeams() {
    return this.api.get(`/api/teams/`)
    .pipe(
      map(response => {
        return response;
      })
    );
  }

  getTeamById(teamId: string) {
    return this.api.get(`/api/teams/?team_id=${teamId}`)
    .pipe(
      map(response => {
        if (response && response.results && response.results.length > 0) {
          return response;
        }
      })
    );
  }

  // add user to team
  addUsertoTeam(teamDetails: DataObject) {
    return this.api.post(`/api/teams/${teamDetails.teamId}/add-member/`, {
      'broker': teamDetails.teamMembers[0].employeeNumber,
      'role': 'member'
    })
    .pipe(
      map(response => {
        return response;
      })
    );
  }

  // delete user from team
  deleteUserFromTeam(teamId: string, brokerId: string) {
    const requestOptions = {
      body: {
        'member_id': brokerId
      }
    };
    return this.api.delete(`/api/teams/${teamId}/remove-member/`, requestOptions)
    .pipe(
      map(response => {
        return response;
      })
    );
  }

  addTeam(teamName: string) {
    return this.api.post(`/api/teams/`, { 'name': teamName })
    .pipe(
      map(response => {
        return response;
      })
    );
  }

  updateTeamName(team: DataObject) {
    return this.api.put(`/api/teams/${team.id}/`, { 'name': team.name });
  }

  assignTrasaction(transaction: DataObject) {
    const transactionEndpoint = this.api.getTransactionEndpoint(transaction);
    return this.api.put(`/api/transactions/${transactionEndpoint}/${transaction?.id}/`, transaction);
  }

  // setupTeamAlert() {
  //   let isLeader = 0;
  //   return Observable.create(observer => {
  //     // Getting Teams to know if the user is a leader
  //     this.getTeams().subscribe(teams => {
  //       if (teams && teams.results && teams.results.length > 0) {
  //         // If user has clicked on NOT SHOW AGAIN
  //         if (this.getNeverShowSetupTeamAlert() !== 'false') {
  //           teams.results.map(team => {
  //             if (team.role && team.role === 'leader' && team.brokers.length === 1) {
  //               isLeader++; // is Leader and doesn't have any broker on his teams
  //             }
  //           });
  //           if (isLeader > 0) {
  //             this.setLocalSetupTeamAlert('true');
  //           } else {
  //             this.setLocalSetupTeamAlert('false');
  //           }
  //         } else {
  //           this.setLocalSetupTeamAlert('false');
  //         }
  //       } else {
  //         this.setLocalSetupTeamAlert('false');
  //       }
  //       observer.next({ 'isLeader': isLeader, 'showSetupTeamAlert': this.getLocalSetupTeamAlert() });
  //       observer.complete();
  //     });
  //   });
  // }

  // setLocalSetupTeamAlert(value) {
  //   window.localStorage.setItem('showSetupTeamAlert', value);
  // }

  // getLocalSetupTeamAlert() {
  //   return window.localStorage.getItem('showSetupTeamAlert');
  // }

  // setNeverShowSetupTeamAlert(value) {
  //   window.localStorage.setItem('neverShowSetupTeamAlert', value);
  // }

  // getNeverShowSetupTeamAlert() {
  //   const neverShowSetupTeamAlert = window.localStorage.getItem('neverShowSetupTeamAlert');
  //   if (neverShowSetupTeamAlert === null) {
  //     this.setNeverShowSetupTeamAlert('true');
  //   }
  //   return window.localStorage.getItem('neverShowSetupTeamAlert');
  // }

  getProfilePicture(employeeId: string): Observable<any> {
    if (this.auth.getAuthToken()) {
      return this.api.get(`/api/activedirectory/fetchphoto/${employeeId}/`)
        .pipe(
          map((response: Response) => {
            return response;
          })
        );
    }
    return of('');
  }

  setEmployeeNumberLocalStorage(value: string): void {
    window.localStorage.setItem('employeeNumber', value);
  }
  getEmployeeNumberLocalStorage(): string {
    return window.localStorage.getItem('employeeNumber') || '';
  }
  setCurrencySymbolLocalStorage(value: string): void {
    window.localStorage.setItem('currency_symbol', value);
  }
  getCurrencySymbolLocalStorage(): string {
    return window.localStorage.getItem('currency_symbol') || '';
  }
  setCurrencyLabelLocalStorage(value: string): void {
    window.localStorage.setItem('currency_label', value);
  }
  getCurrencyLabelLocalStorage(): string {
    return window.localStorage.getItem('currency_label') || '';
  }
  setCurrencyDecimalsLocalStorage(value: string): void {
    window.localStorage.setItem('currency_decimals', value);
  }
  getCurrencyDecimalsLocalStorage(): string {
    return window.localStorage.getItem('currency_decimals') || '';
  }
  setCountryLocalStorage(value: string): void {
    window.localStorage.setItem('country', value);
  }
  getCountryLocalStorage(): Countries {
    return window.localStorage.getItem('country') as Countries;
  }
  setRoleLocalStorage(value: string): void {
    window.localStorage.setItem('role', value);
  }
  getRoleLocalStorage(): string {
    return window.localStorage.getItem('role') || '';
  }
  setCountryLabelLocalStorage(value: string): void {
    window.localStorage.setItem('country_label', value);
  }
  getCountryLabelLocalStorage(): string {
    return window.localStorage.getItem('country_label') || '';
  }
  setSupportUserFlagStorage(value: string): void {
    window.localStorage.setItem('is_supportuser', value);
  }
  getSupportUserFlagStorage(): string {
    return window.localStorage.getItem('is_supportuser') || '';
  }
  setFinanceUserFlagStorage(value: string): void {
    window.localStorage.setItem('is_financeuser', value);
  }
  getFinanceUserFlagStorage(): string {
    return window.localStorage.getItem('is_financeuser') || '';
  }
  setMasterUserFlagStorage(value: string): void {
    window.localStorage.setItem('is_masteruser', value);
  }
  getMasterUserFlagStorage(): string {
    return window.localStorage.getItem('is_masteruser') || '';
  }
  setSuperUserFlagStorage(value: string): void {
    window.localStorage.setItem('is_superuser', value);
  }
  setBusinessUserFlagStorage(value: string): void {
    window.localStorage.setItem('is_businessuser', value);
  }
  getSuperUserFlagStorage(): string {
    return window.localStorage.getItem('is_superuser') || '';
  }
  getBusinessUserFlagStorage(): string {
    return window.localStorage.getItem('is_businessuser') || '';
  }
  setWD3blockUpdateStorage(value: string): void {
    window.localStorage.setItem('block_wd3_updatebutton', value);
  }
  getWD3blockUpdateStorage(): string {
    return window.localStorage.getItem('block_wd3_updatebutton') || '';
  }
  setLocale(value: string): void {
    window.localStorage.setItem('user_locale', value);
  }
  getLocale(): string {
    return window.localStorage.getItem('user_locale') || 'en-US';
  }
  setBlockSubmit(value: string): void {
    window.localStorage.setItem('block_submit', value);
  }
  getBlockSubmit(): boolean {
    return window.localStorage.getItem('block_submit') === 'true';
  }
  getUserRoles(): DataObject[] {
    return [
      {
        'key': 'financial_user',
        'value': this.getFinanceUserFlagStorage()
      },
      {
        'key': 'support_user',
        'value': this.getSupportUserFlagStorage()
      },
      {
        'key': 'master_user',
        'value': this.getMasterUserFlagStorage()
      },
      {
        'key': 'super_user',
        'value': this.getSuperUserFlagStorage()
      },
      {
        'key': 'business_user',
        'value': this.getBusinessUserFlagStorage()
      }
    ];
  }
  getClassToDisableScreen(transaction?: any): string {
    let className = 'fl-page__container';
    if (this.getFinanceUserFlagStorage() === 'true' && transaction.can_edit === false) {
      className = 'fl-page__container d-layer';
    } else if (this.getSupportUserFlagStorage() === 'true') {
      className = 'fl-page__container d-layer';
    }
    if (transaction.status_name === 'Adjust Bill Lines'
      || transaction.access_to_adjust_bill_lines === true
      || transaction.latest_non_error_status === 19
    ) {
      className = 'fl-page__container d-layer';
    }
    return className;
  }

  showLicenseNumber(transactionType: string, country: string): boolean {
    let showLicenseNumber = false;
    switch (transactionType) {
      case 'sale':
      case 'lease':
        if (country === 'HKG') {
          showLicenseNumber = true;
        }
        break;
      default:
        break;
    }
    return showLicenseNumber;
  }
  showSMF(country: string): boolean {
    let hideSMF = false;
    switch (country) {
      case Countries.India:
      case Countries.Japan:
      case Countries.Singapore:
      case Countries.Indonesia:
      case Countries.Hongkong:
      case Countries.China:
      case Countries.Thailand:
      case Countries.Philippines:
      case Countries.Vietnam:
      case Countries.Korea:
      case Countries.Taiwan:
        hideSMF = true;
        break;
      default:
        break;
    }
    return hideSMF;
  }

  showIntermediaryQuestion(transaction: any, country: string): boolean {
    let showIntermediaryQuestion = false;
    switch (country) {
      case Countries.China:
      case Countries.Hongkong:
      case Countries.Taiwan:
        if (transaction.intermediary) {
          showIntermediaryQuestion = true;
        }
        break;
      default:
        showIntermediaryQuestion = false;
        break;
    }
    return showIntermediaryQuestion;
  }

  getTeamNames = memoizeObservable((teamId: number | null = null, queryString?: string) => {
    /*
      INFO: When passing the TeamID, that particular team will be included in the payload.
    */
    let url = `/api/teams/getteams/`;
    if (teamId && !queryString) {
      url += `?team_id=${teamId}`;
    }
    if (queryString && !teamId) {
      url += `?q=${queryString}`;
    }
    if (teamId && queryString) {
      url += `?team_id=${teamId}&queryString=${queryString}`;
    }
    return this.api.get(url)
    .pipe(
      map(response => {
        if (response && response.options) {
          return response.options;
        }
      })
    );
  });

  setCountryListLocalStorage(value: string): void {
    window.localStorage.setItem('user_country_list', JSON.stringify(value));
  }

  getUserCountryListLocalStorage() {
    return JSON.parse(window.localStorage.getItem('user_country_list') || '');
  }

  setUserLocaleBaseOnCountry({ country = '', country_label = '', current_language = '', country_list = [] }) {
    const countryLocale: DataObject = country_list.filter((countryItem: DataObject) =>
      `${countryItem?.value}` === `${country}` &&
      `${countryItem?.label}` === `${country_label}` &&
      `${countryItem?.lang}` === `${current_language}`
    )[0];
    if (countryLocale) {
      this.setLocale(countryLocale.locale);
    } else {
      this.setLocale('en-US');
    }
  }

  setUserLanguageLocalStorage(lang: string): void {
    window.localStorage.setItem('user_language', lang);
  }

  getUserLanguageLocalStorage(): string {
    return window.localStorage.getItem('user_language') || '';
  }

  setCEPIntegration(value: string): void {
    window.localStorage.setItem('CEP_integration', value);
  }

  getCEPIntegration(): boolean {
    return window.localStorage.getItem('CEP_integration') === 'true';
  }

  setExecutiveApprover(value: string): void {
    window.localStorage.setItem('is_executive_approver', value);
  }

  getExecutiveApprover(): boolean {
    return window.localStorage.getItem('is_executive_approver') === 'true';
  }

  setExecutiveApprover1(value: string): void {
    window.localStorage.setItem('is_executive_approver1', value);
  }

  getExecutiveApprover1(): boolean {
    return window.localStorage.getItem('is_executive_approver1') === 'true';
  }

  setExecutiveApprover2(value: string): void {
    window.localStorage.setItem('is_executive_approver2', value);
  }

  getExecutiveApprover2(): boolean {
    return window.localStorage.getItem('is_executive_approver2') === 'true';
  }

  setSurveyFlag(value: string): void {
    window.localStorage.setItem('is_survey_due', value);
  }

  getSurveyFlag(): boolean {
    return window.localStorage.getItem('is_survey_due') === 'true';
  }

  setClientIQIntegration(profileDetails: any) {
    [
      TransactionTypes.LEASE,
      TransactionTypes.SALE,
      TransactionTypes.CONSULTATION,
      TransactionTypes.DSF,
      TransactionTypes.VALUATION
    ].forEach(opportunityType => {
      window.localStorage.setItem(`is_${opportunityType}_ciq_integration`, profileDetails[`is_${opportunityType}_ciq_integration`]);
    });
  }

  getClientIQIntegration(opportunityType: string): boolean {
    if (opportunityType === 'consulting') {
      opportunityType = 'consultation';
    }
    return window.localStorage.getItem(`is_${opportunityType}_ciq_integration`) === 'true';
  }

  setSalesForceAPI(value: string): void {
    window.localStorage.setItem('is_salesforce_api', value);
  }

  getSalesForceAPI(): boolean {
    return window.localStorage.getItem('is_salesforce_api') === 'true';
  }

  setMyFinanceLocalStorage(value: string): void {
    window.localStorage.setItem('my_finance', value);
  }

  getMyFinanceLocalStorage(): string {
    return window.localStorage.getItem('my_finance') || '';
  }

  setDeeplinkingCountryLocalStorage(country: string): void {
    window.localStorage.setItem('deeplinking_country', country);
  }

  getDeeplinkingCountryLocalStorage(): string {
    return window.localStorage.getItem('deeplinking_country') || '';
  }

  setCEPApiIntegration(value: string): void {
    window.localStorage.setItem('cep_api_integrations', value);
  }

  getCEPApiIntegration(): boolean {
    return window.localStorage.getItem('cep_api_integrations') === 'true';
  }

  isPacificIsolationEnable(transaction?: DataObject): boolean {
    let isEnable = false;
    if (transaction?.transaction_type === TransactionTypes.SALE) {
      if (
        this.getCEPIntegration() &&
        !transaction?.is_hotels_business_line &&
        [Countries.Australia, Countries.NewZealand, Countries.India].includes(this.getCountryLocalStorage()) &&
        this.getCEPApiIntegration()
      ) {
        isEnable = true;
      } else if (
        (this.getCEPIntegration() && transaction?.is_hotels_business_line) ||
        !this.getCEPIntegration() ||
        !this.getCEPApiIntegration()
      ) {
        isEnable = false;
      }
    } else if (transaction) {
      if (
        this.getCEPIntegration() &&
        [Countries.Australia, Countries.NewZealand, Countries.India].includes(this.getCountryLocalStorage()) &&
        (['lease'].includes(transaction?.transaction_type)) &&
        this.getCEPApiIntegration()
      ) {
        isEnable = true;
      } else if (
        (this.getCEPIntegration() && [Countries.Australia, Countries.NewZealand, Countries.India].includes(this.getCountryLocalStorage())) ||
        !this.getCEPIntegration() ||
        !this.getCEPApiIntegration()
      ) {
        isEnable = false;
      }
    } else {
      if (
        this.getCEPIntegration() &&
        [Countries.Australia, Countries.NewZealand, Countries.India].includes(this.getCountryLocalStorage()) &&
        this.getCEPApiIntegration()
      ) {
        isEnable = true;
      } else if (!this.getCEPIntegration() || !this.getCEPApiIntegration()) {
        isEnable = false;
      }
    }
    return isEnable;
  }

  setInvoiceAddressFormUpdate(value: string): void {
    window.localStorage.setItem('invoice_address_form', value);
  }

  getInvoiceAddressFormUpdate(): boolean {
    return window.localStorage.getItem('invoice_address_form') === `${window.localStorage.getItem('country')}|true`;
  }

  setIsMultiLaEnabled(value: string): void {
    window.localStorage.setItem('is_multi_la_enabled', value);
  }

  getIsMultiLaEnabled(): boolean {
    return window.localStorage.getItem('is_multi_la_enabled') === 'true';
  }

  setIsValuationEnabled(value: string): void {
    window.localStorage.setItem('is_valuation_enabled', value);
  }

  getIsValuationEnabled(): boolean {
    return window.localStorage.getItem('is_valuation_enabled') === 'true';
  }

  setIsDataCenterEnabled(value: string): void {
    window.localStorage.setItem('is_data_center_enabled', value);
  }

  getIsDataCenterEnabled(): boolean {
    return window.localStorage.getItem('is_data_center_enabled') === 'true';
  }

  getTeamsClientIQIntegration(opportunityType: string, team: TeamsResponse): boolean {
    if (opportunityType === TransactionTypes.CONSULTING) {
      opportunityType = TransactionTypes.CONSULTATION;
    }
    return team && team[`${opportunityType.toLowerCase()}_ciq_integration`] === true;
  }

  isUserAdminRole(): boolean {
    return this.getRoleLocalStorage() === 'Admin';
  }

  isUserFinanceRole(): boolean {
    return this.getFinanceUserFlagStorage() === 'true';
  }

  isBusinessUserRole(): boolean {
    return this.getBusinessUserFlagStorage() === 'true';
  }

  setUserEmailLocalStorage(email: string): void {
    window.localStorage.setItem('user_email', email);
  }

  getUserEmailLocalStorage(): string {
    return window.localStorage.getItem('user_email') || '';
  }
  shouldDisableActionsForFinanceUser(transactionStatus?: string): boolean {
    if (!transactionStatus) {
      return false;
    }
    return this.isUserFinanceRole() && transactionStatus.toLowerCase() === TransactionStatus.DENIED.toLowerCase();
  }
  shouldHideDeleteForFinanceUser(transactionStatus?: string): boolean {
    return this.shouldDisableActionsForFinanceUser(transactionStatus);
  }
}
